<!-- Copyright (c) 2009-2022. Authors: see NOTICE file.

 Licensed under the Apache License, Version 2.0 (the "License");
 you may not use this file except in compliance with the License.
 You may obtain a copy of the License at

      http://www.apache.org/licenses/LICENSE-2.0

 Unless required by applicable law or agreed to in writing, software
 distributed under the License is distributed on an "AS IS" BASIS,
 WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 See the License for the specific language governing permissions and
 limitations under the License.-->

<template>
<ims-modal :active="active" :title="$t('download-annotations-title')" @close="close">
  <div class="download-annotations-body">
    <b-field>
      <b-radio-button v-model="mode" native-value="per-user" type="is-link">
        {{ $t('delete-annotations-mode-per-user') }}
      </b-radio-button>
      <b-radio-button v-model="mode" native-value="all" type="is-link">
        {{ $t('delete-annotations-mode-all') }}
      </b-radio-button>
    </b-field>

    <b-field v-if="mode === 'per-user'" :label="$t('delete-annotations-select-users')">
      <domain-tag-input
        v-model="selectedUsers"
        :domains="members"
        placeholder="search-user"
        displayedProperty="fullName"
        searchedProperty="fullName"
      />
    </b-field>

    <p v-else class="all-note">
      {{ $t('download-annotations-all-note') }}
    </p>
  </div>

  <template #footer>
    <button class="button" @click="close">
      {{ $t('button-cancel') }}
    </button>
    <button class="button is-link" :disabled="!canDownload || downloading" :class="{'is-loading': downloading}" @click="download">
      {{ $t('button-download') }}
    </button>
  </template>
</ims-modal>
</template>

<script>
import {Ims} from '@/api';
import ImsModal from '@/components/utils/ImsModal';
import DomainTagInput from '@/components/utils/DomainTagInput';

export default {
  name: 'download-annotations-modal',
  components: {
    ImsModal,
    DomainTagInput,
  },
  props: {
    active: {type: Boolean, default: false},
    image: {type: Object, required: true}
  },
  data() {
    return {
      mode: 'per-user',
      selectedUsers: [],
      downloading: false
    };
  },
  computed: {
    members() {
      return this.$store.state.currentProject.members || [];
    },
    canDownload() {
      return this.mode === 'all' || this.selectedUsers.length > 0;
    }
  },
  watch: {
    active(val) {
      if (val) {
        this.mode = 'per-user';
        this.selectedUsers = [];
        this.downloading = false;
      }
    }
  },
  methods: {
    close() {
      this.$emit('update:active', false);
    },
    async download() {
      if (!this.canDownload || this.downloading) {
        return;
      }
      this.downloading = true;
      try {
        let allUsers = this.mode === 'all';
        let payload = {
          image: this.image.id,
          allUsers,
          users: allUsers ? [] : this.selectedUsers.map(u => u.id)
        };
        let response = await Ims.instance.api.post('annotation/export.json', payload, {
          responseType: 'blob'
        });
        let filename = this.filenameFromResponse(response) || `annotations_image_${this.image.id}.json`;
        this.triggerBlobDownload(response.data, filename);
        this.$notify({type: 'success', text: this.$t('notif-success-annotations-downloaded')});
        this.close();
      } catch (error) {
        console.log(error);
        this.$notify({type: 'error', text: this.$t('notif-error-annotations-download')});
      } finally {
        this.downloading = false;
      }
    },
    filenameFromResponse(response) {
      let cd = response.headers && response.headers['content-disposition'];
      if (!cd) {
        return null;
      }
      let match = cd.match(/filename\s*=\s*"?([^";]+)"?/i);
      return match ? match[1].trim() : null;
    },
    triggerBlobDownload(blob, filename) {
      const blobUrl = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(blobUrl);
    }
  }
};
</script>

<style scoped>
.download-annotations-body {
  min-height: 8rem;
}

>>> .modal-card, >>> .modal-card-body {
  overflow: visible !important;
  width: 50vw !important;
}
</style>
